import { create } from 'zustand';
import type { Contact } from '@/types';

interface ContactsState {
  contacts: Contact[];
  isLoading: boolean;
  searchTerm: string;
  selectedRegion: number | null;
  
  // Actions
  setContacts: (contacts: Contact[]) => void;
  addContact: (contact: Omit<Contact, 'id'>) => void;
  updateContact: (id: number, updates: Partial<Contact>) => void;
  deleteContact: (id: number) => void;
  setSearchTerm: (term: string) => void;
  setSelectedRegion: (region: number | null) => void;
  
  // Computed
  filteredContacts: () => Contact[];
  getContactById: (id: number) => Contact | undefined;
  getContactsByRegion: (regionId: number) => Contact[];
}

export const useContactsStore = create<ContactsState>((set, get) => ({
  contacts: [],
  isLoading: false,
  searchTerm: '',
  selectedRegion: null,

  setContacts: (contacts) => set({ contacts }),

  addContact: (contactData) => {
    const newContact: Contact = {
      ...contactData,
      id: Date.now(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    set(state => ({ contacts: [...state.contacts, newContact] }));
  },

  updateContact: (id, updates) => {
    set(state => ({
      contacts: state.contacts.map(contact =>
        contact.id === id
          ? { ...contact, ...updates, updated_at: new Date().toISOString() }
          : contact
      )
    }));
  },

  deleteContact: (id) => {
    set(state => ({
      contacts: state.contacts.filter(contact => contact.id !== id)
    }));
  },

  setSearchTerm: (searchTerm) => set({ searchTerm }),
  setSelectedRegion: (selectedRegion) => set({ selectedRegion }),

  filteredContacts: () => {
    const { contacts, searchTerm, selectedRegion } = get();
    
    return contacts.filter(contact => {
      const matchesSearch = !searchTerm || 
        contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.phone1?.includes(searchTerm);
      
      const matchesRegion = selectedRegion == null || contact.region_id === selectedRegion;
      
      return matchesSearch && matchesRegion;
    });
  },

  getContactById: (id) => {
    return get().contacts.find(contact => contact.id === id);
  },

  getContactsByRegion: (regionId) => {
    return get().contacts.filter(contact => contact.region_id === regionId);
  },
}));
